import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Upload, Eye, Edit3, Columns2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import BlockRenderer from "../../components/BlockRenderer";
import RichTextEditor from "../../components/RichTextEditor";
import { plainToHtml } from "../../components/RichText";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const BLOCK_TYPES = [
  { value: "hero", label: "Hero (image + title)" },
  { value: "heading", label: "Heading" },
  { value: "text", label: "Text paragraph" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video (YouTube / Vimeo)" },
  { value: "quote", label: "Quote" },
  { value: "stats", label: "Stats bar (burgundy)" },
  { value: "service_grid", label: "Service grid (cards)" },
  { value: "callout", label: "Callout banner" },
  { value: "two_column", label: "Two columns (image + text)" },
  { value: "pricing", label: "Pricing comparison" },
  { value: "checklist", label: "Checklist (with checkmarks)" },
];

const emptyBlock = (type) => {
  const base = { id: Math.random().toString(36).slice(2, 10), type };
  switch (type) {
    case "hero": return { ...base, data: { eyebrow: "", title: "New hero title", subtitle: "", image_path: "", cta_text: "", cta_link: "" } };
    case "heading": return { ...base, data: { text: "New heading", level: "h2" } };
    case "text": return { ...base, data: { content: "Write your content here..." } };
    case "image": return { ...base, data: { path: "", caption: "", alt: "" } };
    case "video": return { ...base, data: { embed_url: "", caption: "" } };
    case "quote": return { ...base, data: { text: "An inspiring quote.", author: "" } };
    case "stats": return { ...base, data: { items: [{ value: "40+", label: "Years experience" }, { value: "90%", label: "Success rate" }] } };
    case "service_grid": return { ...base, data: { items: [{ title: "Service", excerpt: "Short description", link: "/services", image_url: "" }] } };
    case "callout": return { ...base, data: { eyebrow: "", title: "Ready to begin?", text: "Reach out and let's start the conversation.", cta_text: "Contact us", cta_link: "/contact", variant: "burgundy" } };
    case "two_column": return { ...base, data: { eyebrow: "", title: "Section title", text: "Section description", cta_text: "", cta_link: "", image_url: "", image_path: "", side: "right" } };
    case "pricing": return { ...base, data: { industry_label: "Industry Average", industry_value: "$5,000 – $20,000", industry_caption: "What most agencies charge", our_label: "Spirit & Associates", our_value: "$2,500", our_caption: "Complete professional service", savings: "Save up to $17,500" } };
    case "checklist": return { ...base, data: { eyebrow: "What's included", title: "Everything you need — nothing held back.", items: [{ text: "First item" }, { text: "Second item" }, { text: "Third item" }] } };
    default: return { ...base, data: {} };
  }
};

function ImagePicker({ value, onChange, label = "Image" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.storage_path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-start gap-4 mt-2">
        {value ? (
          <img src={fileUrl(value)} alt="" className="w-32 h-24 object-cover border border-neutral-200" />
        ) : (
          <div className="w-32 h-24 border border-dashed border-neutral-300 bg-neutral-50 flex items-center justify-center text-xs text-neutral-400">No image</div>
        )}
        <div className="space-y-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline-burgundy text-xs px-4 py-2">
            <Upload size={12} /> {uploading ? "Uploading..." : "Upload image"}
          </button>
          {value && <button type="button" onClick={() => onChange("")} className="block text-xs text-neutral-500 hover:text-red-600">Remove</button>}
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, onChange, onDelete, onMoveUp, onMoveDown }) {
  const update = (patch) => onChange({ ...block, data: { ...block.data, ...patch } });

  return (
    <div className="block-card" data-testid={`block-editor-${block.type}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="label-eyebrow">{block.type}</span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} className="p-1.5 hover:bg-neutral-100 rounded" title="Move up"><ArrowUp size={14} /></button>
          <button onClick={onMoveDown} className="p-1.5 hover:bg-neutral-100 rounded" title="Move down"><ArrowDown size={14} /></button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded" title="Delete" data-testid="delete-block-btn"><Trash2 size={14} /></button>
        </div>
      </div>

      {block.type === "hero" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Eyebrow text</Label><Input value={block.data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} /></div>
          <div><Label>CTA label</Label><Input value={block.data.cta_text || ""} onChange={(e) => update({ cta_text: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Title</Label><Input value={block.data.title || ""} onChange={(e) => update({ title: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Subtitle</Label><Textarea value={block.data.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} rows={2} /></div>
          <div><Label>CTA link</Label><Input value={block.data.cta_link || ""} onChange={(e) => update({ cta_link: e.target.value })} placeholder="/contact" /></div>
          <div><Label>Image URL (or upload below)</Label><Input value={block.data.image_url || ""} onChange={(e) => update({ image_url: e.target.value })} placeholder="https://..." /></div>
          <div className="sm:col-span-2"><ImagePicker value={block.data.image_path} onChange={(v) => update({ image_path: v })} label="Background image" /></div>
        </div>
      )}

      {block.type === "heading" && (
        <div className="space-y-3">
          <div><Label>Text</Label><Input value={block.data.text || ""} onChange={(e) => update({ text: e.target.value })} /></div>
          <div>
            <Label>Level</Label>
            <Select value={block.data.level || "h2"} onValueChange={(v) => update({ level: v })}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="h1">H1 (Largest)</SelectItem>
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {block.type === "text" && (
        <div>
          <Label>Content</Label>
          <RichTextEditor
            value={plainToHtml(block.data.content || "")}
            onChange={(html) => update({ content: html })}
          />
        </div>
      )}

      {block.type === "image" && (
        <div className="space-y-3">
          <ImagePicker value={block.data.path} onChange={(v) => update({ path: v })} />
          <div><Label>Caption</Label><Input value={block.data.caption || ""} onChange={(e) => update({ caption: e.target.value })} /></div>
          <div><Label>Alt text</Label><Input value={block.data.alt || ""} onChange={(e) => update({ alt: e.target.value })} /></div>
        </div>
      )}

      {block.type === "video" && (
        <div className="space-y-3">
          <div>
            <Label>YouTube or Vimeo URL</Label>
            <Input value={block.data.embed_url || ""} onChange={(e) => update({ embed_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="video-url-input" />
            <p className="text-xs text-neutral-500 mt-1">Paste any YouTube or Vimeo URL — we'll embed it automatically.</p>
          </div>
          <div><Label>Caption</Label><Input value={block.data.caption || ""} onChange={(e) => update({ caption: e.target.value })} /></div>
        </div>
      )}

      {block.type === "quote" && (
        <div className="space-y-3">
          <div><Label>Quote</Label><Textarea value={block.data.text || ""} onChange={(e) => update({ text: e.target.value })} rows={3} /></div>
          <div><Label>Author</Label><Input value={block.data.author || ""} onChange={(e) => update({ author: e.target.value })} /></div>
        </div>
      )}

      {block.type === "stats" && (
        <div className="space-y-3">
          <Label>Stat items</Label>
          {(block.data.items || []).map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><Input placeholder="Value (40+)" value={it.value || ""} onChange={(e) => {
                const items = [...(block.data.items || [])]; items[i] = { ...items[i], value: e.target.value }; update({ items });
              }} /></div>
              <div className="col-span-7"><Input placeholder="Label" value={it.label || ""} onChange={(e) => {
                const items = [...(block.data.items || [])]; items[i] = { ...items[i], label: e.target.value }; update({ items });
              }} /></div>
              <button className="col-span-1 p-2 text-neutral-400 hover:text-red-600" onClick={() => {
                const items = [...(block.data.items || [])]; items.splice(i, 1); update({ items });
              }}><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => update({ items: [...(block.data.items || []), { value: "", label: "" }] })} className="btn-outline-burgundy text-xs px-4 py-2">
            <Plus size={12} /> Add stat
          </button>
        </div>
      )}

      {block.type === "service_grid" && (
        <div className="space-y-4">
          <Label>Service cards</Label>
          {(block.data.items || []).map((it, i) => (
            <div key={i} className="border border-neutral-200 p-4 rounded">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Title" value={it.title || ""} onChange={(e) => {
                  const items = [...(block.data.items || [])]; items[i] = { ...items[i], title: e.target.value }; update({ items });
                }} />
                <Input placeholder="Link (e.g. /intervention)" value={it.link || ""} onChange={(e) => {
                  const items = [...(block.data.items || [])]; items[i] = { ...items[i], link: e.target.value }; update({ items });
                }} />
                <Input className="sm:col-span-2" placeholder="Image URL (optional)" value={it.image_url || ""} onChange={(e) => {
                  const items = [...(block.data.items || [])]; items[i] = { ...items[i], image_url: e.target.value }; update({ items });
                }} />
                <div className="sm:col-span-2">
                  <ImagePicker
                    value={it.image_path}
                    onChange={(v) => {
                      const items = [...(block.data.items || [])]; items[i] = { ...items[i], image_path: v }; update({ items });
                    }}
                    label="Or upload image"
                  />
                </div>
                <Textarea className="sm:col-span-2" placeholder="Short excerpt" rows={2} value={it.excerpt || ""} onChange={(e) => {
                  const items = [...(block.data.items || [])]; items[i] = { ...items[i], excerpt: e.target.value }; update({ items });
                }} />
              </div>
              <button className="mt-2 text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" onClick={() => {
                const items = [...(block.data.items || [])]; items.splice(i, 1); update({ items });
              }}><Trash2 size={12} /> Remove card</button>
            </div>
          ))}
          <button onClick={() => update({ items: [...(block.data.items || []), { title: "New service", excerpt: "", link: "", image_url: "" }] })} className="btn-outline-burgundy text-xs px-4 py-2">
            <Plus size={12} /> Add card
          </button>
        </div>
      )}

      {block.type === "callout" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Eyebrow</Label><Input value={block.data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} /></div>
          <div>
            <Label>Variant</Label>
            <Select value={block.data.variant || "burgundy"} onValueChange={(v) => update({ variant: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="burgundy">Burgundy (dark)</SelectItem>
                <SelectItem value="light">Light burgundy</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Title</Label><Input value={block.data.title || ""} onChange={(e) => update({ title: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Text</Label><Textarea rows={3} value={block.data.text || ""} onChange={(e) => update({ text: e.target.value })} /></div>
          <div><Label>CTA label</Label><Input value={block.data.cta_text || ""} onChange={(e) => update({ cta_text: e.target.value })} /></div>
          <div><Label>CTA link</Label><Input value={block.data.cta_link || ""} onChange={(e) => update({ cta_link: e.target.value })} placeholder="/contact" /></div>
        </div>
      )}

      {block.type === "two_column" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Eyebrow</Label><Input value={block.data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} /></div>
          <div>
            <Label>Image side</Label>
            <Select value={block.data.side || "right"} onValueChange={(v) => update({ side: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Image on left</SelectItem>
                <SelectItem value="right">Image on right</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Title</Label><Input value={block.data.title || ""} onChange={(e) => update({ title: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Text</Label><Textarea rows={4} value={block.data.text || ""} onChange={(e) => update({ text: e.target.value })} /></div>
          <div><Label>CTA label</Label><Input value={block.data.cta_text || ""} onChange={(e) => update({ cta_text: e.target.value })} /></div>
          <div><Label>CTA link</Label><Input value={block.data.cta_link || ""} onChange={(e) => update({ cta_link: e.target.value })} /></div>
          <div><Label>Image URL</Label><Input value={block.data.image_url || ""} onChange={(e) => update({ image_url: e.target.value })} /></div>
          <div className="sm:col-span-2"><ImagePicker value={block.data.image_path} onChange={(v) => update({ image_path: v })} label="Or upload image" /></div>
        </div>
      )}

      {block.type === "pricing" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 text-xs text-neutral-500 -mb-2">Left side (industry average — gets struck through)</div>
          <div><Label>Industry label</Label><Input value={block.data.industry_label || ""} onChange={(e) => update({ industry_label: e.target.value })} placeholder="Industry Average" /></div>
          <div><Label>Industry value</Label><Input value={block.data.industry_value || ""} onChange={(e) => update({ industry_value: e.target.value })} placeholder="$5,000 – $20,000" /></div>
          <div className="sm:col-span-2"><Label>Industry caption (optional)</Label><Input value={block.data.industry_caption || ""} onChange={(e) => update({ industry_caption: e.target.value })} placeholder="What most agencies charge" /></div>
          <div className="sm:col-span-2 text-xs text-neutral-500 mt-2 -mb-2">Right side (your price — burgundy)</div>
          <div><Label>Your label</Label><Input value={block.data.our_label || ""} onChange={(e) => update({ our_label: e.target.value })} placeholder="Spirit & Associates" /></div>
          <div><Label>Your value</Label><Input value={block.data.our_value || ""} onChange={(e) => update({ our_value: e.target.value })} placeholder="$2,500" /></div>
          <div className="sm:col-span-2"><Label>Your caption (optional)</Label><Input value={block.data.our_caption || ""} onChange={(e) => update({ our_caption: e.target.value })} placeholder="Complete professional service" /></div>
          <div className="sm:col-span-2 mt-2"><Label>Savings banner (across the bottom)</Label><Input value={block.data.savings || ""} onChange={(e) => update({ savings: e.target.value })} placeholder="Save up to $17,500" /></div>
        </div>
      )}

      {block.type === "checklist" && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Eyebrow (small text above)</Label><Input value={block.data.eyebrow || ""} onChange={(e) => update({ eyebrow: e.target.value })} placeholder="What's included" /></div>
            <div><Label>Title</Label><Input value={block.data.title || ""} onChange={(e) => update({ title: e.target.value })} placeholder="Everything you need — nothing held back." /></div>
          </div>
          <div>
            <Label>Checklist items</Label>
            <div className="space-y-2 mt-2">
              {(block.data.items || []).map((it, i) => {
                const text = typeof it === "string" ? it : (it.text || "");
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Input className="flex-1" value={text} onChange={(e) => {
                      const items = [...(block.data.items || [])];
                      items[i] = { text: e.target.value };
                      update({ items });
                    }} placeholder={`Item ${i + 1}`} />
                    <button onClick={() => {
                      const items = [...(block.data.items || [])]; items.splice(i, 1); update({ items });
                    }} className="p-2 text-neutral-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                );
              })}
              <button onClick={() => update({ items: [...(block.data.items || []), { text: "" }] })} className="btn-outline-burgundy text-xs px-4 py-2">
                <Plus size={12} /> Add item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPageEditor() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState("text");
  const [viewMode, setViewMode] = useState("split"); // "edit" | "split" | "preview"

  useEffect(() => {
    api.get("/pages").then(r => {
      const p = (r.data || []).find(x => x.id === pageId);
      if (!p) { toast.error("Page not found"); navigate("/admin/pages"); return; }
      setPage(p);
    });
  }, [pageId, navigate]);

  if (!page) return <div className="p-10 text-neutral-500">Loading…</div>;

  const updateField = (k, v) => setPage({ ...page, [k]: v });

  const updateBlock = (idx, b) => {
    const next = [...page.blocks]; next[idx] = b; setPage({ ...page, blocks: next });
  };
  const addBlock = () => setPage({ ...page, blocks: [...(page.blocks || []), emptyBlock(addType)] });
  const deleteBlock = (idx) => {
    const next = [...page.blocks]; next.splice(idx, 1); setPage({ ...page, blocks: next });
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= page.blocks.length) return;
    const next = [...page.blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPage({ ...page, blocks: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/pages/${page.id}`, {
        slug: page.slug,
        title: page.title,
        nav_label: page.nav_label,
        in_nav: page.in_nav,
        order: page.order,
        seo_description: page.seo_description,
        blocks: page.blocks,
      });
      toast.success("Page saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`p-4 sm:p-6 lg:p-8 ${viewMode === "preview" ? "max-w-none" : "max-w-none"}`} data-testid="admin-page-editor">
      <div className="flex items-center justify-between mb-6 sticky top-14 lg:top-0 bg-[#F7F7F7] z-30 py-3 -my-3 flex-wrap gap-3">
        <div>
          <Link to="/admin/pages" className="label-eyebrow text-[#6B1F2A] inline-flex items-center gap-2 mb-2"><ArrowLeft size={12} /> Back to pages</Link>
          <h1 className="font-serif-display text-3xl text-[#1a1a1a]">Edit page</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center bg-white border border-neutral-200 rounded-md p-0.5 mr-2" data-testid="view-mode-toggle">
            <button onClick={() => setViewMode("edit")} title="Editor only"
              className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "edit" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              data-testid="view-mode-edit"><Edit3 size={12} /> Edit</button>
            <button onClick={() => setViewMode("split")} title="Side-by-side"
              className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "split" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              data-testid="view-mode-split"><Columns2 size={12} /> Split</button>
            <button onClick={() => setViewMode("preview")} title="Preview only"
              className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 transition-colors ${viewMode === "preview" ? "bg-[#6B1F2A] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              data-testid="view-mode-preview"><Eye size={12} /> Preview</button>
          </div>
          <Link to={`/${page.slug === "home" ? "" : page.slug}`} target="_blank" className="btn-outline-burgundy" data-testid="preview-page-btn"><Eye size={14} /> Live</Link>
          <button onClick={save} disabled={saving} className="btn-primary-burgundy disabled:opacity-60" data-testid="save-page-btn">{saving ? "Saving..." : "Save changes"}</button>
        </div>
      </div>

      <div className={`grid gap-6 ${viewMode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {viewMode !== "preview" && (
          <div className="min-w-0 max-w-3xl" data-testid="editor-pane">
            <div className="bg-white border border-neutral-200 p-6 mb-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={page.title} onChange={(e) => updateField("title", e.target.value)} data-testid="edit-page-title" /></div>
                <div><Label>URL slug</Label><Input value={page.slug} onChange={(e) => updateField("slug", e.target.value)} disabled={page.is_system} /></div>
                <div><Label>Nav label</Label><Input value={page.nav_label || ""} onChange={(e) => updateField("nav_label", e.target.value)} /></div>
                <div><Label>Order</Label><Input type="number" value={page.order} onChange={(e) => updateField("order", parseInt(e.target.value, 10) || 0)} /></div>
                <div className="flex items-center justify-between sm:col-span-2">
                  <Label>Show in navigation</Label>
                  <Switch checked={page.in_nav} onCheckedChange={(v) => updateField("in_nav", v)} />
                </div>
                <div className="sm:col-span-2"><Label>SEO description</Label><Textarea value={page.seo_description || ""} onChange={(e) => updateField("seo_description", e.target.value)} rows={2} /></div>
              </div>
            </div>

            <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-serif-display text-2xl text-[#1a1a1a]">Content blocks</h2>
              <div className="flex items-center gap-2">
                <Select value={addType} onValueChange={setAddType}>
                  <SelectTrigger className="w-52" data-testid="add-block-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOCK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={addBlock} className="btn-primary-burgundy" data-testid="add-block-btn"><Plus size={14} /> Add</button>
              </div>
            </div>

            <div>
              {(page.blocks || []).map((b, i) => (
                <BlockEditor key={b.id || i}
                  block={b}
                  onChange={(nb) => updateBlock(i, nb)}
                  onDelete={() => deleteBlock(i)}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, 1)} />
              ))}
              {(!page.blocks || page.blocks.length === 0) && (
                <div className="text-center py-12 border border-dashed border-neutral-300 rounded text-neutral-500" data-testid="no-blocks-empty">
                  No content blocks yet. Add one using the dropdown above.
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode !== "edit" && (
          <div className="min-w-0" data-testid="preview-pane">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 mb-3">
                <span className="label-eyebrow text-neutral-500">Live preview</span>
                <span className="text-xs text-neutral-400">— updates as you type, save to publish</span>
              </div>
              <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="bg-neutral-100 border-b border-neutral-200 px-4 py-2 flex items-center gap-2 text-xs text-neutral-500">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                  <span className="ml-2 truncate">/{page.slug === "home" ? "" : page.slug}</span>
                </div>
                <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
                  <BlockRenderer blocks={page.blocks || []} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
